import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Search, RefreshCw, Star, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface Post {
  id: number;
  title: string;
  content: string;
  author_id: number;
  author_nickname?: string;
  category_id: number;
  category_name?: string;
  is_essence: number;
  is_top: string;
  views: number;
  likes: number;
  replies_count: number;
  created_at: string;
}

const AdminExperience: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    keyword: '',
    category: ''
  });
  const [showEssence, setShowEssence] = useState<'all' | 'essence'>('all');
  const [showTop, setShowTop] = useState<'all' | 'global' | 'category'>('all');
  const pageSize = 20;

  useEffect(() => {
    loadPosts();
  }, [page, showEssence, showTop, filters]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      if (filters.keyword) params.append('keyword', filters.keyword);
      if (filters.category) params.append('category_id', filters.category);
      if (showEssence === 'essence') params.append('is_essence', '1');
      if (showTop === 'global') params.append('is_top', 'global');
      if (showTop === 'category') params.append('is_top', 'category');

      const response = await api.get(`/posts/admin/list?${params}`);

      if (response.data?.success && response.data.data) {
        setPosts(response.data.data.posts || []);
        setTotal(response.data.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('加载心得列表失败:', error);
      toast.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEssence = async (postId: number, isEssence: number) => {
    try {
      const res = await api.put(`/posts/${postId}/essence`, {
        is_essence: isEssence ? 0 : 1
      });

      if (res.data?.success) {
        toast.success(isEssence ? '已取消精华' : '已设为精华');
        loadPosts();
      }
    } catch (error) {
      console.error('设置精华失败:', error);
      toast.error('操作失败');
    }
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm('确定要删除这篇心得吗？此操作不可撤销。')) return;

    try {
      const res = await api.delete(`/posts/${postId}`);

      if (res.data?.success) {
        toast.success('删除成功');
        loadPosts();
      }
    } catch (error) {
      console.error('删除失败:', error);
      toast.error('删除失败');
    }
  };

  const formatTime = (time: string) => {
    return new Date(time).toLocaleString('zh-CN');
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">心得管理</h1>
        <button
          onClick={loadPosts}
          className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
        >
          <RefreshCw className="w-4 h-4" />
          刷新
        </button>
      </div>

      {/* 筛选和标签切换 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          {/* 标签切换 */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowEssence('all')}
              className={`px-4 py-2 rounded-lg font-medium ${
                showEssence === 'all'
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              全部心得
            </button>
            <button
              onClick={() => setShowEssence('essence')}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                showEssence === 'essence'
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Star className="w-4 h-4" />
              精华心得
            </button>
          </div>

          {/* 搜索框 */}
          <div className="flex gap-2">
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
              placeholder="搜索标题或内容"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            <button
              onClick={() => setPage(1)}
              className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
            >
              搜索
            </button>
          </div>
        </div>
      </div>

      {/* 数据表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">标题</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">作者</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">版块</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">数据</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">发布时间</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin text-pink-500" />
                  </td>
                </tr>
              ) : posts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">暂无数据</td>
                </tr>
              ) : (
                posts.map((post) => (
                  <tr key={post.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{post.id}</td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        {post.is_top === 'global' && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">全站置顶</span>
                        )}
                        {post.is_top === 'category' && (
                          <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">板块置顶</span>
                        )}
                        {post.is_essence === 1 && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                        <span className="font-medium">{post.title}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">{post.author_name || post.author_nickname || `用户${post.author_id}`}</td>
                    <td className="py-3 px-4 text-sm">{post.category_name || `版块${post.category_id}`}</td>
                    <td className="py-3 px-4 text-sm">
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>👁️ {post.views}</div>
                        <div>❤️ {post.likes}</div>
                        <div>💬 {post.replies_count}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">{formatTime(post.created_at)}</td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleEssence(post.id, post.is_essence)}
                          className={`p-2 rounded-lg ${
                            post.is_essence === 1
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={post.is_essence === 1 ? '取消精华' : '设为精华'}
                        >
                          <Star className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
            <div className="text-sm text-gray-600">
              共 {total} 条记录，第 {page} / {Math.ceil(total / pageSize)} 页
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                上一页
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / pageSize)}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminExperience;
